import { QUERY_KEYS } from "@/libraries/constant";
import { getIsoDateTime } from "@/libraries/date";
import { randInt } from "@/libraries/helper";
import type { PresignPayloadSchema } from "@/schemas";
import { storageService } from "@/services";
import type { GetStoragesResponse } from "@/types";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";

export const storageMutations = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_UPLOAD_IMAGES], {
		mutationFn: (payloads: PresignPayloadSchema[]) =>
			storageService.create(payloads),
		retry: 2,
		onMutate: async (newStorages) => {
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_STORAGES],
			});
			const optimisticFiles = newStorages.map((f) => ({
				id: randInt(),
				file_id: URL.createObjectURL(f.file),
				created_at: getIsoDateTime(),
				updated_at: getIsoDateTime(),
			}));
			queryClient.setQueryData<InfiniteData<GetStoragesResponse>>(
				[QUERY_KEYS.GET_STORAGES],
				(prev): InfiniteData<GetStoragesResponse> => {
					const firstPage = prev?.pages[0];
					if (!firstPage)
						return {
							pageParams: [1],
							pages: [
								{
									storages: optimisticFiles,
									meta: {
										current_page: 1,
										next_page: null,
										prev_page: null,
										total_count: optimisticFiles.length,
										total_pages: 1,
									},
								},
							],
						};
					firstPage.storages = firstPage.storages.concat(optimisticFiles);
					return {
						pageParams: prev?.pageParams ?? [],
						pages: [firstPage, ...(prev?.pages ?? []).slice(1)],
					};
				},
			);

			return { optimisticFiles };
		},
		onSuccess: (res, _, ctx) => {
			const { optimisticFiles } = ctx;
			if (!optimisticFiles) return;
			queryClient.setQueryData<InfiniteData<GetStoragesResponse>>(
				[QUERY_KEYS.GET_STORAGES],
				(prev) => {
					const firstPage = prev?.pages[0];
					if (!firstPage) {
						return {
							pageParams: [1],
							pages: [
								{
									storages: res.storages,
									meta: {
										current_page: 1,
										next_page: null,
										prev_page: null,
										total_count: res.storages.length,
										total_pages: 1,
									},
								},
							],
						};
					}
					const filteredStorages = firstPage.storages.filter(
						(s) => !optimisticFiles.some((o) => o.id === s.id),
					);
					firstPage.storages = filteredStorages.concat(res.storages);
					return {
						pageParams: prev?.pageParams ?? [],
						pages: [firstPage, ...(prev?.pages ?? []).slice(1)],
					};
				},
			);
		},
		onError: (_err, _variables, ctx) => {
			const optimisticFiles = ctx?.optimisticFiles;
			if (!optimisticFiles) return;
			queryClient.setQueryData<InfiniteData<GetStoragesResponse>>(
				[QUERY_KEYS.GET_STORAGES],
				(prev) => {
					const firstPage = prev?.pages[0];
					if (!firstPage) {
						return {
							pageParams: prev?.pageParams ?? [],
							pages: [],
						};
					}
					const filteredStorages = firstPage.storages.filter(
						(s) => !optimisticFiles.some((o) => o.id === s.id),
					);
					firstPage.storages = filteredStorages;
					return {
						pageParams: prev?.pageParams ?? [],
						pages: [firstPage, ...(prev?.pages ?? []).slice(1)],
					};
				},
			);
		},
	});
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_DELETE_IMAGE], {
		mutationFn: (id: number) => storageService.remove(id),
		retry: 2,
		onMutate: async (id) => {
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_STORAGES],
			});
			const currentStorages = queryClient.getQueryData<
				InfiniteData<GetStoragesResponse>
			>([QUERY_KEYS.GET_STORAGES]);
			const pageIdx = currentStorages?.pages?.findIndex((page) =>
				page.storages.some((s) => s.id === id),
			);
			if (pageIdx === undefined || pageIdx < 0) return;
			const currentStorage = currentStorages?.pages?.[pageIdx]?.storages?.find(
				(s) => s.id === id,
			);
			queryClient.setQueryData<InfiniteData<GetStoragesResponse>>(
				[QUERY_KEYS.GET_STORAGES],
				(prev) => ({
					pageParams: prev?.pageParams ?? [],
					pages:
						prev?.pages?.map((page, pIdx) => {
							if (pageIdx !== pIdx) return page;
							return {
								meta: {
									...page.meta,
									total_count: page.meta.total_count - 1,
								},
								storages: page.storages.filter((s) => s.id !== id),
							};
						}) ?? [],
				}),
			);
			return { id, pageIdx, currentStorage };
		},
		onError: (_err, _variables, ctx) => {
			const id = ctx?.id;
			const pageIdx = ctx?.pageIdx;
			const currentStorage = ctx?.currentStorage;
			if (!id || !pageIdx || !currentStorage) return;
			queryClient.setQueryData<InfiniteData<GetStoragesResponse>>(
				[QUERY_KEYS.GET_STORAGES],
				(prev) => ({
					pageParams: prev?.pageParams ?? [],
					pages:
						prev?.pages?.map((page, pIdx) => {
							if (pageIdx !== pIdx) return page;
							page.storages.push(currentStorage);
							return page;
						}) ?? [],
				}),
			);
		},
	});
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_DELETE_IMAGES], {
		mutationFn: (ids: number[]) => storageService.removes(ids),
		retry: 2,
		onMutate: async (ids) => {
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_STORAGES],
			});
			const currentStorages = queryClient.getQueryData<
				InfiniteData<GetStoragesResponse>
			>([QUERY_KEYS.GET_STORAGES]);
			const pageIdxs = currentStorages?.pages?.map((page, pIdx) =>
				page.storages.some((s) => ids.includes(s.id)) ? pIdx : -1,
			);
			const currentStoragesFiltered = currentStorages?.pages?.map(
				(page, pIdx) => {
					if (pageIdxs?.includes(pIdx)) {
						return {
							meta: {
								...page.meta,
								total_count: page.meta.total_count - ids.length,
							},
							storages: page.storages.filter((s) => !ids.includes(s.id)),
						};
					}
					return page;
				},
			);
			queryClient.setQueryData<InfiniteData<GetStoragesResponse>>(
				[QUERY_KEYS.GET_STORAGES],
				(prev) => ({
					pageParams: prev?.pageParams ?? [],
					pages: currentStoragesFiltered ?? [],
				}),
			);
			return { ids, pageIdxs };
		},
		onError: (_err, _variables, ctx) => {
			const ids = ctx?.ids;
			const pageIdxs = ctx?.pageIdxs;
			if (!ids || !pageIdxs) return;
			queryClient.setQueryData<InfiniteData<GetStoragesResponse>>(
				[QUERY_KEYS.GET_STORAGES],
				(prev) => ({
					pageParams: prev?.pageParams ?? [],
					pages:
						prev?.pages?.map((page, pIdx) => {
							if (pageIdxs.includes(pIdx)) {
								page.storages.push(
									...page.storages.filter((s) => ids.includes(s.id)),
								);
							}
							return page;
						}) ?? [],
				}),
			);
		},
	});
};
